import dayjs from 'dayjs';
import { DaySequence, DaySequenceStatus } from '@core/generated/gql-types';
import { useJobStore } from '@core/job/useJobStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import { useClientStore } from '@core/client/useClientStore';

export const useDaySequenceUtils = () => {
  const getJobById = useJobStore((state) => state.getJobById);
  const getWorkerById = useWorkerStore((state) => state.getWorkerById);
  const getClientById = useClientStore((state) => state.getClientById);

  const getStatusBadgeClass = (status: DaySequenceStatus) => {
    switch (status) {
      case DaySequenceStatus.ProposedChange:
        return 'bg-orange-100 text-orange-800';
      case DaySequenceStatus.EscalatedToTenant:
        return 'bg-red-100 text-red-800';
      case DaySequenceStatus.RequiresApproval:
      case DaySequenceStatus.InProgress:
        return 'bg-blue-100 text-blue-800';
      case DaySequenceStatus.Pending:
        return 'bg-gray-100 text-yellow-800';
      case DaySequenceStatus.ReadyForPay:
      case DaySequenceStatus.PayProcessed:
      case DaySequenceStatus.Completed:
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: DaySequenceStatus) => {
    switch (status) {
      case DaySequenceStatus.ProposedChange:
        return 'Proposed Change';
      case DaySequenceStatus.EscalatedToTenant:
        return 'Disputed';
      case DaySequenceStatus.RequiresApproval:
        return 'Requires Approval';
      case DaySequenceStatus.InProgress:
        return 'In Progress';
      case DaySequenceStatus.Pending:
        return 'Pending';
      case DaySequenceStatus.ReadyForPay:
        return 'Ready for Pay';
      case DaySequenceStatus.PayProcessed:
        return 'Pay Processed';
      case DaySequenceStatus.Completed:
        return 'Completed';
      case DaySequenceStatus.Cancelled:
        return 'Cancelled';
      case DaySequenceStatus.Unfilled:
        return 'Unfilled';
      default:
        return status;
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(parseInt(dateString, 10)).format('D MMM');
  };

  const getWorkerName = (daySequence: DaySequence) => {
    if (daySequence.userId) {
      const worker = getWorkerById(daySequence.userId);
      if (worker) {
        const name =
          `${worker.firstName || ''} ${worker.lastName || ''}`.trim();
        return name || 'Unknown Worker';
      }
    }
    return 'Unknown Worker';
  };

  const getClientName = (daySequence: DaySequence) => {
    if (daySequence.clientId) {
      const client = getClientById(daySequence.clientId);
      return client?.name || 'Unknown Client';
    }
    const job = daySequence.jobId ? getJobById(daySequence.jobId) : null;
    if (job?.clientId) {
      const client = getClientById(job.clientId);
      return client?.name || 'Unknown Client';
    }
    return 'Unknown Client';
  };

  return {
    getStatusBadgeClass,
    getStatusLabel,
    formatDate,
    getWorkerName,
    getClientName,
  };
};
